// src/pages/documents/FieldMappingPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Spin, 
  Alert, 
  Empty, 
  Breadcrumb,
  Space,
  Divider,
  message,
  Tooltip
} from 'antd';
import { 
  ScanOutlined, 
  ArrowLeftOutlined, 
  ReloadOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { PdfFieldMapping } from '../../types';
import { probateDocsService } from './probatedocs.service';
import FieldMappingList from './FieldMappingList';
import PdfPreviewModal from './PdfPreviewModal';

const { Title, Text } = Typography;

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: auto;
  padding: 24px;
  background: #ffffff;
  border-radius: 8px;
  min-height: calc(100vh - 200px);
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 16px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  border: 1px solid #C7D2FE;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  h4 {
    margin: 0 0 8px 0;
    color: #4338CA;
  }
  
  p {
    margin: 0;
    color: #6366F1;
    font-size: 14px;
  }
`;

const ToolsBar = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #F9FAFB;
  border-radius: 8px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const ToolsLabel = styled.span`
  font-size: 13px;
  color: #6B7280;
  font-weight: 500;
`;

interface FieldMappingPageProps {
  // Optional props if passed directly instead of via route
  probateDocId?: string;
}

const FieldMappingPage: React.FC<FieldMappingPageProps> = (props) => {
  // Get probateDocId from route params or props
  const { probateDocId: routeDocId } = useParams<{ probateDocId: string }>();
  const probateDocId = props.probateDocId || routeDocId || '';
  
  const navigate = useNavigate();

  // State
  const [fields, setFields] = useState<PdfFieldMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExtracted, setHasExtracted] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Fetch existing field mappings
  const fetchMappings = useCallback(async () => {
    if (!probateDocId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await probateDocsService.getFieldMappings(probateDocId);
      setFields(data);
      if (data.length > 0) {
        setHasExtracted(true);
      }
    } catch (err: any) {
      console.error('Failed to fetch mappings:', err);
      // If we get a 404, it means no fields have been extracted yet
      if (err.response?.status === 404) {
        setFields([]);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch field mappings');
      }
    } finally {
      setLoading(false);
    }
  }, [probateDocId]);

  // Extract fields from PDF
  const extractFields = useCallback(async () => {
    if (!probateDocId) return;
    
    setExtracting(true);
    setError(null);
    try {
      const response = await probateDocsService.extractFields(probateDocId);
      setFields(response.fields);
      setHasExtracted(true);
      message.success('Fields extracted successfully');
    } catch (err: any) {
      console.error('Failed to extract fields:', err);
      setError(err.response?.data?.message || 'Failed to extract fields from PDF');
    } finally {
      setExtracting(false);
    }
  }, [probateDocId]);

  // Download annotated PDF
  const handleDownloadAnnotated = useCallback(async () => {
    if (!probateDocId) return;
    
    setDownloading(true);
    try {
      await probateDocsService.downloadAnnotatedPdf(probateDocId);
      message.success('Annotated PDF downloaded successfully');
    } catch (err: any) {
      console.error('Failed to download annotated PDF:', err);
      message.error(err.response?.data?.message || 'Failed to download annotated PDF');
    } finally {
      setDownloading(false);
    }
  }, [probateDocId]);

  // Navigate to visual PDF mapping page
  const handleOpenVisualMapper = useCallback(() => {
    navigate(`/probate-docs/${probateDocId}/visual-mapper`);
  }, [navigate, probateDocId]);

  // Initial load
  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/probate-docs');
  };

  // Render loading state
  if (loading && !hasExtracted) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spin size="large" />
          <Text style={{ marginTop: 16 }}>Loading field mappings...</Text>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Breadcrumb Navigation */}
      <StyledBreadcrumb>
        <Breadcrumb.Item>
          <a onClick={handleBack}>Probate Documents</a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Field Mapping</Breadcrumb.Item>
      </StyledBreadcrumb>

      {/* Page Header */}
      <PageHeader>
        <HeaderContent>
          <Space wrap align="start">
            <Button 
              aria-label="Back to probate documents" icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              type="text"
            />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <FileTextOutlined style={{ marginRight: 8 }} />
                Map PDF Fields
              </Title>
              <Text type="secondary">
                Map form fields to database entities for autofill
              </Text>
            </div>
          </Space>
        </HeaderContent>
        <HeaderActions>
          {fields.length > 0 && (
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchMappings}
              loading={loading}
            >
              Refresh
            </Button>
          )}
          <Button 
            type="primary"
            icon={<ScanOutlined />}
            onClick={extractFields}
            loading={extracting}
          >
            {fields.length > 0 ? 'Re-extract Fields' : 'Extract Fields'}
          </Button>
        </HeaderActions>
      </PageHeader>

      {/* Tools Bar - shown when fields exist */}
      {fields.length > 0 && (
        <ToolsBar>
          <ToolsLabel>Visual Aids:</ToolsLabel>
          <Tooltip title="Download a PDF with field markers to help identify field locations">
            <Button 
              icon={<DownloadOutlined />}
              onClick={handleDownloadAnnotated}
              loading={downloading}
            >
              Download Annotated PDF
            </Button>
          </Tooltip>
          <Tooltip title="Open interactive PDF viewer to map fields visually">
            <Button 
              icon={<EyeOutlined />}
              onClick={handleOpenVisualMapper}
              type="primary"
              ghost
            >
              Visual PDF Mapper
            </Button>
          </Tooltip>
          <Tooltip title="Preview prefilled PDF with active estate data">
            <Button 
              icon={<EyeOutlined />}
              onClick={() => setPreviewVisible(true)}
            >
              Preview Filled PDF
            </Button>
          </Tooltip>
        </ToolsBar>
      )}

      <Divider />

      {/* Error Alert */}
      {error && (
        <Alert 
          message="Error" 
          description={error} 
          type="error" 
          showIcon 
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Info Card */}
      {fields.length === 0 && !extracting && (
        <InfoCard>
          <h4>How Field Mapping Works</h4>
          <p>
            1. Click "Extract Fields" to scan the PDF and identify all fillable form fields.<br />
            2. Each field will be displayed with its name and surrounding context text.<br />
            3. Map each field to the appropriate database entity and field (e.g., User → First Name).<br />
            4. Once mapped, the system can auto-fill these fields with user data when generating documents.
          </p>
        </InfoCard>
      )}

      {/* Extracting State */}
      {extracting && (
        <LoadingContainer>
          <Spin size="large" />
          <Text style={{ marginTop: 16 }}>
            Extracting fields from PDF...
          </Text>
          <Text type="secondary" style={{ marginTop: 8 }}>
            This may take a moment depending on the document size.
          </Text>
        </LoadingContainer>
      )}

      {/* Empty State */}
      {!extracting && fields.length === 0 && (
        <EmptyStateContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                No fields have been extracted yet.<br />
                Click "Extract Fields" to scan the PDF document.
              </span>
            }
          >
            <Button 
              type="primary"
              icon={<ScanOutlined />}
              onClick={extractFields}
            >
              Extract Fields
            </Button>
          </Empty>
        </EmptyStateContainer>
      )}

      {/* Field Mapping List */}
      {!extracting && fields.length > 0 && (
        <FieldMappingList 
          fields={fields}
          probateDocId={probateDocId}
          onUpdate={fetchMappings}
        />
      )}

      {probateDocId && (
        <PdfPreviewModal
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          probateDocId={probateDocId}
        />
      )}
    </PageContainer>
  );
};

export default FieldMappingPage;